// Spiegelt den Vorfilter des Servers (services/vat.ts VAT_ID_FORMAT). Bewusst eine KOPIE, mit
// derselben Begruendung, unter der sie in `AuthorForm` stand: Sie waehlt hier nur die Wortwahl,
// die bindende Pruefung bleibt auf dem Server.
import { fill, CLIENT_VAT_MARK_TEXTS as T } from '../shared/texts'

export const VAT_ID_FORMAT = /^([A-Z]{2})([A-Z0-9]{2,14})$/
export const normalizeVatId = (v: string) => v.toUpperCase().replace(/\s+/g, '').replace(/[.\-/]/g, '')

// Der Zustand der USt-IdNr. als Zeichen samt Satz dazu — an EINER Stelle.
//
// ⛔ Herausgezogen am 11.09.2026 aus `AuthorForm` und `AuthorsList`, als der Zustand eine
// dritte Stelle bekam (die Monatsabrechnung). Zwei Fassungen desselben Urteils sind genau die
// Stelle, an der Anzeige und Geschehen auseinanderlaufen — und sie liefen bereits
// auseinander: Die Liste kannte den Format-Fall nicht und sagte „Noch nicht bestätigt" zu
// einem Eintrag, den VIES nie zu sehen bekommen hatte.
//
// ⚠️ Jede Lage hat ein EIGENES Zeichen, nicht nur eine eigene Farbe. Ein Häkchen in Grün gegen
// eines in Orange wäre für jeden unlesbar, der Farben schlecht unterscheidet — und der
// Unterschied ist hier keiner von Geschmack: Er sagt, ob der Befund gespeichert ist. Aus
// demselben Grund ersetzt der Tooltip das Zeichen nicht: Auf dem Telefon ist er nicht
// erreichbar.

/** Was hier gebraucht wird — nicht mehr. Autorendatensatz, Listenzeile und der Snapshot einer
 *  Abrechnung tragen dieselben drei Angaben, also passen alle drei hier hinein. */
export interface VatStand {
    vatId?: string
    vatIdValid?: boolean
    vatIdCheckedAt?: string
    /** ⚠️ Kommt vom SERVER (`authorTaxCase`), nie aus dem Land abgeleitet — sonst stuende die
     *  Steuerregel ein zweites Mal im Client. Fehlt er, gilt der strengere Auslandsfall. */
    taxCase?: string
}

// ⛔ IM SITZLAND SAGT DIESE SPALTE NICHTS UEBER GELD. Ein zypriotischer Autor braucht keine
// USt-IdNr. (Betreiber, 10.09.2026), also blockiert auch keine fehlende oder abgelehnte etwas.
// Bis zum 11.09.2026 stand trotzdem „keine Auszahlung moeglich" daneben — in der
// Autoren-Abrechnung sogar direkt neben dem funktionierenden Knopf „Als bezahlt markieren".
// Eine falsche Behauptung ueber Geld, gefunden, als das Zeichen in die dritte Maske kam und
// Zeichen und Knopf zum ersten Mal nebeneinander standen.
//
// ⚠️ Das Zeichen wird deshalb GEKLAMMERT, nicht nur umgefaerbt: „(✗)" liest sich als „gilt
// hier nicht", und zwar auch fuer den, der Rot von Grau nicht unterscheidet. Die Hausregel
// „jede Lage ein eigenes Zeichen" gilt hier doppelt — „abgelehnt, mit Folge" und „abgelehnt,
// ohne Folge" sind zwei Lagen, nicht zwei Farben derselben.
const imSitzland = (a: VatStand | null | undefined) => a?.taxCase === 'domestic-cy'

export function vatSatz(a: VatStand | null | undefined): string {
    // Im Sitzland zuerst: Jeder Satz darunter nennt eine Folge, die es hier nicht gibt.
    if (imSitzland(a)) {
        if (!a?.vatId) return T.domesticNoVatId
        if (a.vatIdValid === false) return T.domesticRejected
        if (a.vatIdValid === true) {
            const when = a.vatIdCheckedAt ? new Date(a.vatIdCheckedAt).toLocaleDateString('de-DE') : ''
            return fill(T.domesticConfirmed, { when: when ? fill(T.confirmedWhen, { when }) : '' })
        }
        return T.domesticUnconfirmed
    }
    if (!a?.vatId) return T.noVatId
    if (a.vatIdValid === true) {
        const when = a.vatIdCheckedAt ? new Date(a.vatIdCheckedAt).toLocaleDateString('de-DE') : ''
        return fill(T.confirmed, { when: when ? fill(T.confirmedWhen, { when }) : '' })
    }
    // Before blaming VIES, say whether it was even asked. A value that fails the format gate
    // never reached it — typically because something other than a VAT id ended up in the field.
    if (!VAT_ID_FORMAT.test(normalizeVatId(a.vatId))) {
        return T.unreadable
    }
    if (a.vatIdValid === false) return T.rejected
    return T.unconfirmed
}

export function vatZeichen(a: VatStand | null | undefined): { zeichen: string, farbe: string, titel: string } {
    const titel = vatSatz(a)
    if (imSitzland(a)) {
        // Geklammert und grau: die Lage stimmt, die Folge fehlt.
        if (a?.vatIdValid === true) return { zeichen: '(✓)', farbe: 'var(--muted)', titel }
        if (a?.vatIdValid === false) return { zeichen: '(✗)', farbe: 'var(--muted)', titel }
        return { zeichen: '(○)', farbe: 'var(--muted)', titel }
    }
    if (!a?.vatId) return { zeichen: '○', farbe: 'var(--muted)', titel }
    if (a.vatIdValid === true) return { zeichen: '✓', farbe: 'var(--ok)', titel }
    if (!VAT_ID_FORMAT.test(normalizeVatId(a.vatId))) return { zeichen: '!', farbe: 'var(--warn)', titel }
    if (a.vatIdValid === false) return { zeichen: '✗', farbe: 'var(--danger)', titel }
    return { zeichen: '○', farbe: 'var(--muted)', titel }
}

/** Das Zeichen als fertiges Element, wo nur „hinschreiben" gebraucht wird. */
export default function VatMark({ stand }: { stand: VatStand | null | undefined }) {
    const { zeichen, farbe, titel } = vatZeichen(stand)
    // `title` traegt den Satz; `aria-label` dasselbe, damit der Screenreader nicht nur das
    // Zeichen vorliest.
    return <span title={titel} aria-label={titel} style={{ color: farbe, fontWeight: 700 }}>{zeichen}</span>
}
